/**
 * OrderActionsMenu — the "Actions" dropdown on the V2 Order Details view.
 *
 * Also owns the override-payment-status flow shared between V1 and V2:
 *   - `useOverridePaymentStatusFlow` — picked-status state, mutation, modal.
 *     The modal is returned to the caller so it survives the menu's
 *     close-on-pick cycle.
 *   - `OverridePaymentStatusButton` — V1 standalone trigger.
 *   - The V2 submenu trigger lives inline below as `OverridePaymentStatusSubmenu`.
 */
import { Button, Tooltip, tokens } from "@blurb/codex-react";
import { Menu, Stack, createStyles } from "@mantine/core";
import { GraphQL, GraphQLQueryData, isGraphQLError } from "@rpi/openapi-api";
import { IconV3, RpiButton, useToasts } from "@rpi/openapi-core";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { Body } from "../../components/Typography";
import { ConfirmationModalV2 } from "../../components/modals/ConfirmationModalV2";
import { useMenuStyles } from "../../components/menuStyles";
import { useAuthUser } from "../../hooks/useAuthUser";
import { canCancelOrder } from "./cancelEligibility";

type Order = GraphQLQueryData<GraphQL.OrderMinimalQuery>;

interface OrderActionsMenuProps {
  order: Order;
  onReprintClick: () => void;
  onRefundClick: () => void;
  onCancelClick: () => void;
  /** Confirms an admin override of the payment status. The actual modal is
   *  hoisted in the parent so it survives the menu's close-on-pick cycle. */
  onOverridePaymentStatusPick: (status: GraphQL.PaymentStatus) => void;
}

export function OrderActionsMenu({
  order,
  onReprintClick,
  onRefundClick,
  onCancelClick,
  onOverridePaymentStatusPick,
}: OrderActionsMenuProps) {
  const user = useAuthUser();
  const { classes: menuClasses } = useMenuStyles();

  const canAccessOrderActions = user.is.SUPER_ADMIN || user.is.CUSTOMER_ADMIN;
  if (!canAccessOrderActions) return null;

  const isCancellable = order ? canCancelOrder(order) : false;
  const isCancelled = order?.status === GraphQL.OrderStatus.Cancelled;
  const refundDisabled = order?.canRefund !== true;
  const hasAnyAvailableAction = user.is.SUPER_ADMIN || isCancellable;
  const triggerDisabledTooltip = getTriggerDisabledTooltip({ isCancelled, hasAnyAvailableAction });

  const trigger = (
    <Button variant="filled" intent="primary" size="large" disabled={triggerDisabledTooltip !== null}>
      <IconV3 icon="add" size={24} color={tokens.color.semantic.icon.inverse} />
      Actions
    </Button>
  );

  if (triggerDisabledTooltip) {
    return (
      <Tooltip content={triggerDisabledTooltip}>
        <div>{trigger}</div>
      </Tooltip>
    );
  }

  return (
    <Menu withinPortal={true} classNames={menuClasses}>
      <Menu.Target>{trigger}</Menu.Target>
      <Menu.Dropdown>
        {user.is.SUPER_ADMIN && (
          <>
            <Menu.Item
              icon={<IconV3 icon="print" size={20} aria-hidden="true" />}
              role="menuitem"
              disabled={!order?.canReprint}
              onClick={() => {
                if (order?.canReprint) onReprintClick();
              }}
            >
              Reprint
            </Menu.Item>
            <Menu.Item
              data-testid="refund-order-menu-item"
              icon={<IconV3 icon="payment_arrow_down" size={20} aria-hidden="true" />}
              role="menuitem"
              disabled={refundDisabled}
              onClick={refundDisabled ? undefined : onRefundClick}
            >
              Refund Order
            </Menu.Item>
            {order?.customerOrderId && order.customer?.publicId && (
              <OverridePaymentStatusSubmenu currentStatus={order.paymentStatus} onPick={onOverridePaymentStatusPick} />
            )}
            <Menu.Divider />
          </>
        )}
        <CancelOrderMenuItem isCancellable={isCancellable} onCancel={onCancelClick} />
      </Menu.Dropdown>
    </Menu>
  );
}

interface CancelOrderMenuItemProps {
  isCancellable: boolean;
  onCancel: () => void;
}

// Codex Tooltip requires a single ReactElement child, and a disabled Mantine
// Menu.Item swallows pointer events — so the tooltip-wrapped branch needs a
// <div> shim to catch hover.
const CancelOrderMenuItem: React.FC<CancelOrderMenuItemProps> = ({ isCancellable, onCancel }) => {
  const item = (
    <Menu.Item
      data-testid="cancel-order-menu-item"
      data-intent-danger
      icon={<IconV3 icon="cancel" size={20} aria-hidden="true" color="inherit" />}
      role="menuitem"
      disabled={!isCancellable}
      onClick={isCancellable ? onCancel : undefined}
    >
      Cancel Order
    </Menu.Item>
  );

  if (isCancellable) return item;

  return (
    <Tooltip content="Cannot Cancel Order">
      <div>{item}</div>
    </Tooltip>
  );
};

function getTriggerDisabledTooltip(params: { isCancelled: boolean; hasAnyAvailableAction: boolean }): string | null {
  if (params.isCancelled) {
    return "This order has been cancelled — no further actions available.";
  }
  if (!params.hasAnyAvailableAction) {
    return "Cannot Cancel Order";
  }
  return null;
}

/* ---------- Override payment status flow ---------- */

const PAYMENT_STATUS_LABELS: Record<GraphQL.PaymentStatus, string> = {
  [GraphQL.PaymentStatus.Paid]: "Paid",
  [GraphQL.PaymentStatus.Unpaid]: "Unpaid",
  [GraphQL.PaymentStatus.PaymentPending]: "Payment pending",
  [GraphQL.PaymentStatus.ExternallyPaid]: "Externally paid",
  [GraphQL.PaymentStatus.NoPaymentStaffOrder]: "No payment (staff order)",
};

const PAYMENT_STATUS_ENTRIES: ReadonlyArray<{ value: GraphQL.PaymentStatus; label: string }> = [
  { value: GraphQL.PaymentStatus.Paid, label: PAYMENT_STATUS_LABELS[GraphQL.PaymentStatus.Paid] },
  { value: GraphQL.PaymentStatus.Unpaid, label: PAYMENT_STATUS_LABELS[GraphQL.PaymentStatus.Unpaid] },
  { value: GraphQL.PaymentStatus.PaymentPending, label: PAYMENT_STATUS_LABELS[GraphQL.PaymentStatus.PaymentPending] },
  { value: GraphQL.PaymentStatus.ExternallyPaid, label: PAYMENT_STATUS_LABELS[GraphQL.PaymentStatus.ExternallyPaid] },
  {
    value: GraphQL.PaymentStatus.NoPaymentStaffOrder,
    label: PAYMENT_STATUS_LABELS[GraphQL.PaymentStatus.NoPaymentStaffOrder],
  },
];

const OVERRIDE_REASON = "Manual override from admin dashboard";

type OverridableOrder = {
  customerOrderId?: string | null;
  customer?: {
    publicId?: string | null;
  } | null;
  paymentStatus?: GraphQL.PaymentStatus | null;
};

interface ConfirmBodyProps {
  customerOrderId: string;
  currentStatus: GraphQL.PaymentStatus | null | undefined;
  newStatus: GraphQL.PaymentStatus;
}

const ConfirmBody: React.FC<ConfirmBodyProps> = ({ customerOrderId, currentStatus, newStatus }) => (
  <Stack spacing="sm">
    <Body>
      Override payment status on order <strong>{customerOrderId}</strong>?
    </Body>
    <Body>
      Current status: <strong>{currentStatus ? PAYMENT_STATUS_LABELS[currentStatus] : "Unknown"}</strong>
    </Body>
    <Body>
      New status: <strong>{PAYMENT_STATUS_LABELS[newStatus]}</strong>
    </Body>
    <Body>This action will be recorded in the audit log along with your user.</Body>
  </Stack>
);

export function useOverridePaymentStatusFlow(order: OverridableOrder) {
  const { showSuccessToast, showErrorToast } = useToasts();
  const queryClient = useQueryClient();
  const [pickedStatus, setPickedStatus] = React.useState<GraphQL.PaymentStatus | null>(null);
  const [error, setError] = React.useState<string | undefined>();

  const { mutate, isLoading } = GraphQL.useAdminOverridePaymentStatusMutation({
    onSuccess: async (data) => {
      const updatedStatus = data.adminOverridePaymentStatus?.paymentStatus;
      const label = updatedStatus ? PAYMENT_STATUS_LABELS[updatedStatus] : "the new value";
      showSuccessToast(`Payment status updated to ${label}`);
      await queryClient.invalidateQueries({ queryKey: ["Order"] });
      setPickedStatus(null);
      setError(undefined);
    },
    onError: (err) => {
      const message = isGraphQLError(err) ? err.message : "Failed to update payment status";
      setError(message);
      showErrorToast(message);
    },
  });

  const openConfirm = (status: GraphQL.PaymentStatus) => {
    setError(undefined);
    setPickedStatus(status);
  };

  const closeConfirm = () => {
    if (isLoading) return;
    setPickedStatus(null);
    setError(undefined);
  };

  const handleConfirm = () => {
    if (!pickedStatus || !order.customer?.publicId || !order.customerOrderId) return;
    mutate({
      input: {
        orderIdentifier: {
          customerOrderId: order.customerOrderId,
          customerPublicId: order.customer.publicId,
        },
        newPaymentStatus: pickedStatus,
        reason: OVERRIDE_REASON,
      },
    });
  };

  const modal = (
    <ConfirmationModalV2
      opened={pickedStatus !== null}
      onClose={closeConfirm}
      onConfirm={handleConfirm}
      isLoading={isLoading}
      error={error}
      title="Override payment status"
      message={
        pickedStatus !== null && order.customerOrderId ? (
          <ConfirmBody
            customerOrderId={order.customerOrderId}
            currentStatus={order.paymentStatus}
            newStatus={pickedStatus}
          />
        ) : null
      }
      confirmLabel="Override"
      cancelLabel="Cancel"
      loadingLabel="Overriding..."
    />
  );

  return { openConfirm, modal };
}

interface StatusItemListProps {
  currentStatus: GraphQL.PaymentStatus | null | undefined;
  onPick: (status: GraphQL.PaymentStatus) => void;
}

const StatusItemList: React.FC<StatusItemListProps> = ({ currentStatus, onPick }) => (
  <>
    {PAYMENT_STATUS_ENTRIES.map(({ value, label }) => {
      const isCurrent = value === currentStatus;
      return (
        <Menu.Item
          key={value}
          data-testid={`override-payment-status-${value}`}
          disabled={isCurrent}
          onClick={() => onPick(value)}
        >
          {isCurrent ? `${label} (current)` : label}
        </Menu.Item>
      );
    })}
  </>
);

interface OverridePaymentStatusSubmenuProps {
  currentStatus: GraphQL.PaymentStatus | null | undefined;
  onPick: (status: GraphQL.PaymentStatus) => void;
}

/** V2 nested hover-submenu rendered inside the Actions menu dropdown. */
const OverridePaymentStatusSubmenu: React.FC<OverridePaymentStatusSubmenuProps> = ({ currentStatus, onPick }) => (
  <Menu trigger="hover" position="right-start" closeOnItemClick withinPortal>
    <Menu.Target>
      <Menu.Item
        data-testid="override-payment-status-menu-item"
        icon={<IconV3 icon="edit" size={20} aria-hidden="true" />}
        rightSection={<IconV3 icon="chevron_right" size={16} aria-hidden="true" />}
        closeMenuOnClick={false}
        role="menuitem"
      >
        Override payment status
      </Menu.Item>
    </Menu.Target>
    <Menu.Dropdown>
      <StatusItemList currentStatus={currentStatus} onPick={onPick} />
    </Menu.Dropdown>
  </Menu>
);

const useButtonStyles = createStyles({
  // Mantine Menu.Target needs a real DOM node to attach its ref to. RpiButton
  // is a function component without forwardRef, so we wrap it in this div.
  // Inline-flex keeps the wrapper sized to its child and not a full-width block.
  triggerWrapper: {
    display: "inline-flex",
  },
});

interface OverridePaymentStatusButtonProps {
  currentStatus: GraphQL.PaymentStatus | null | undefined;
  onPick: (status: GraphQL.PaymentStatus) => void;
}

/** V1 standalone trigger: button + click-opens dropdown of statuses. */
export const OverridePaymentStatusButton: React.FC<OverridePaymentStatusButtonProps> = ({ currentStatus, onPick }) => {
  const { classes } = useButtonStyles();
  return (
    <Menu position="bottom-start" withinPortal>
      <Menu.Target>
        <div className={classes.triggerWrapper} data-testid="override-payment-status-button">
          <RpiButton variant="spark" size="small" width="fit">
            Override Payment Status
          </RpiButton>
        </div>
      </Menu.Target>
      <Menu.Dropdown>
        <StatusItemList currentStatus={currentStatus} onPick={onPick} />
      </Menu.Dropdown>
    </Menu>
  );
};
